import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { AlertTriangle, UserPlus, UserCheck, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/errors";
import { driverAvailabilityApi } from "@/lib/entities";
import { api } from "@/lib/api";
import DriverPickerModal from "./DriverPickerModal";
import CancelUnavailabilityConfirm from "./CancelUnavailabilityConfirm";
import { CoverageNeededBadge } from "./RC10lBadges";

/**
 * RC10l · Coverage Needed card.
 *
 * Rendered above a `TourDriverAssignment` row when
 * `coverage_needed === true`. Exposes three actions, all gated on
 * `perm("fleet.assign")` (Cancel Unavailability additionally
 * requires `perm("drivers.availability")` server-side):
 *
 *   • Find Replacement     — swap driver, preserve role
 *   • Assign Fill-In       — additive coverage driver, original kept
 *   • Cancel Unavailability — delete the underlying unavailable window
 *
 * The card clears from the UI only when the server-returned
 * assignment payload reports `coverage_needed === false`. The caller
 * must therefore refresh the Tour → Drivers list after each action
 * via `onChange`. No optimistic clearing.
 */
export default function CoverageNeededCard({
  tour,
  assignment,          // tour_driver_assignment row (coverage_needed === true)
  onChange,            // async () => void — triggers parent reload
  driverLabel,         // string — the display name for the affected driver
}) {
  const { can } = useAuth();
  const canAssign = can("fleet.assign");
  const canAvailability = can("drivers.availability");

  const [pickerMode, setPickerMode] = useState(null); // "replace" | "fill-in" | null
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const inflight = useRef(false);

  // Resolve the window_id that drives this coverage warning so Cancel
  // Unavailability knows what to delete. The server stores the
  // marker as `driver_unavailable:{window_id}` in `coverage_reason`.
  const windowIdFromReason =
    typeof assignment.coverage_reason === "string"
     && assignment.coverage_reason.startsWith("driver_unavailable:")
      ? assignment.coverage_reason.split(":")[1]
      : null;

  const [activeWindow, setActiveWindow] = useState(null);
  const [loadingWindow, setLoadingWindow] = useState(false);

  // Load the referenced unavailable window so Cancel Unavailability
  // can show accurate dates and reason in the confirmation dialog.
  useEffect(() => {
    let stop = false;
    if (!windowIdFromReason) { setActiveWindow(null); return; }
    setLoadingWindow(true);
    (async () => {
      try {
        const rows = await driverAvailabilityApi.list(assignment.driver_id);
        if (stop) return;
        const match = (rows || []).find((r) => r.id === windowIdFromReason);
        setActiveWindow(match || null);
      } catch {
        // Fall through — card still renders with fallback copy
        setActiveWindow(null);
      } finally {
        if (!stop) setLoadingWindow(false);
      }
    })();
    return () => { stop = true; };
  }, [windowIdFromReason, assignment.driver_id]);

  // Fallback copy when backend did not supply a human-readable reason.
  const humanReason = (() => {
    const r = assignment.coverage_reason;
    if (typeof r === "string" && r.startsWith("driver_unavailable:")) {
      return "Driver marked unavailable for assigned window";
    }
    return r || "Driver marked unavailable for assigned window";
  })();

  const openReplace = () => { if (!canAssign) return; setPickerMode("replace"); };
  const openFillIn = () => { if (!canAssign) return; setPickerMode("fill-in"); };
  const openCancel = () => {
    if (!canAvailability) {
      toast.error("You do not have permission to cancel driver unavailability.");
      return;
    }
    setShowCancelConfirm(true);
  };

  const closePicker = () => setPickerMode(null);

  const confirmCancelUnavailability = async () => {
    if (inflight.current) return;
    if (!windowIdFromReason) {
      toast.error("Cannot determine which Unavailable window to cancel.");
      return;
    }
    inflight.current = true;
    setCancelBusy(true);
    try {
      await driverAvailabilityApi.archive(assignment.driver_id, windowIdFromReason);
      toast.success("Unavailability cancelled");
      setShowCancelConfirm(false);
      await onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || apiError(err));
      toast.error(msg);
    } finally {
      inflight.current = false;
      setCancelBusy(false);
    }
  };

  return (
    <div
      className="border border-amber-hold/70 bg-amber-hold/10 rounded-sm px-3 py-2.5 mb-2"
      data-testid={`coverage-needed-card-${assignment.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle size={16} className="text-amber-hold mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CoverageNeededBadge testid={`coverage-needed-badge-${assignment.id}`} />
              <span
                className="text-[12.5px] text-carbon font-medium truncate"
                data-testid={`coverage-needed-driver-${assignment.id}`}
              >
                {driverLabel || `Driver ${assignment.driver_id.slice(0, 8)}`}
              </span>
            </div>
            <div
              className="mt-0.5 text-[12px] text-carbon"
              data-testid={`coverage-needed-reason-${assignment.id}`}
            >
              Coverage Needed — {humanReason}
            </div>
          </div>
        </div>
        {canAssign && (
          <div
            className="flex flex-col md:flex-row w-full md:w-auto gap-1.5 md:items-center shrink-0"
            data-testid={`coverage-actions-${assignment.id}`}
          >
            <button
              type="button"
              onClick={openReplace}
              data-testid={`coverage-find-replacement-${assignment.id}`}
              className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-[12px] rounded-sm bg-coach-blue text-white hover:bg-deep-road-navy whitespace-nowrap"
            >
              <UserCheck size={12} /> Find Replacement
            </button>
            <button
              type="button"
              onClick={openFillIn}
              data-testid={`coverage-assign-fill-in-${assignment.id}`}
              className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-[12px] rounded-sm border border-coach-blue text-coach-blue bg-white hover:bg-coach-blue/10 whitespace-nowrap"
            >
              <UserPlus size={12} /> Assign Fill-In
            </button>
            <button
              type="button"
              onClick={openCancel}
              disabled={!canAvailability || !windowIdFromReason || loadingWindow}
              data-testid={`coverage-cancel-unavailability-${assignment.id}`}
              className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-[12px] rounded-sm border border-critical-red text-critical-red bg-white hover:bg-critical-red/10 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <XCircle size={12} /> Cancel Unavailability
            </button>
          </div>
        )}
      </div>

      {pickerMode && (
        <DriverPickerModal
          mode={pickerMode}
          tour={tour}
          assignment={assignment}
          onClose={closePicker}
          onDone={onChange}
        />
      )}
      {showCancelConfirm && (
        <CancelUnavailabilityConfirm
          driverId={assignment.driver_id}
          windowId={windowIdFromReason}
          windowStartDate={activeWindow?.start_date || assignment.start_date}
          windowEndDate={activeWindow?.end_date || assignment.end_date}
          windowReason={activeWindow?.reason}
          busy={cancelBusy}
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={confirmCancelUnavailability}
        />
      )}
    </div>
  );
}

// Convenience helper exposed to callers that render the full tour-driver list.
export function hasCoverageNeeded(assignment) {
  return !!assignment && assignment.coverage_needed === true;
}
